/**
 * Formula metric helpers — ids referenced by the active optimizer formula.
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  parseFormula,
  readSubformulaRefId,
  type AstNode,
} from '@trafficpulse/shared';

import type { PreviewBreakdownEntry, PreviewRow } from '../../preview-row.types';

/** Subformula id -> its expression, so `__sub_<id>` refs resolve to real metrics. */
type SubFormulaMap = ReadonlyMap<string, { expression: string }>;

/** Metric ids the formula actually consumes (contributions or expression, with
 *  `__sub_<id>` reference tokens resolved to the subformula's own metrics). */
export function collectFormulaMetricIds(
  breakdown: ReadonlyArray<PreviewBreakdownEntry> | undefined,
  subFormulas?: SubFormulaMap,
): string[] {
  if (!breakdown || breakdown.length === 0) return [];
  const seen = new Set<string>();
  const ordered: string[] = [];
  const add = (id: string) => {
    if (!id || seen.has(id)) return;
    seen.add(id);
    ordered.push(id);
  };
  for (const entry of breakdown) {
    const contribs = entry.contributions ?? [];
    if (contribs.length > 0) {
      for (const c of contribs) add(c.metric);
      continue;
    }
    if (entry.expression) {
      for (const id of metricIdsFromExpression(entry.expression, subFormulas)) {
        add(id);
      }
    }
  }
  return ordered;
}

function metricIdsFromExpression(
  expression: string,
  subFormulas?: SubFormulaMap,
): string[] {
  try {
    const ast = parseFormula(expression);
    const seen = new Set<string>();
    const ordered: string[] = [];
    collectIdents(ast, seen, ordered, subFormulas, new Set());
    return ordered;
  } catch {
    return [];
  }
}

function collectIdents(
  node: AstNode,
  seen: Set<string>,
  ordered: string[],
  subs: SubFormulaMap | undefined,
  visitedSubs: Set<string>,
): void {
  switch (node.kind) {
    case 'ident':
      addIdent(node.name, seen, ordered, subs, visitedSubs);
      return;
    case 'num':
      return;
    case 'unary':
      collectIdents(node.operand, seen, ordered, subs, visitedSubs);
      return;
    case 'binary':
      collectIdents(node.left, seen, ordered, subs, visitedSubs);
      collectIdents(node.right, seen, ordered, subs, visitedSubs);
      return;
    case 'call':
      for (const arg of node.args) {
        collectIdents(arg, seen, ordered, subs, visitedSubs);
      }
  }
}

/** An ident may be a `__sub_<id>` reference — resolve it to the subformula's
 *  own metrics; otherwise it is a real metric id. */
function addIdent(
  name: string,
  seen: Set<string>,
  ordered: string[],
  subs: SubFormulaMap | undefined,
  visitedSubs: Set<string>,
): void {
  const subId = readSubformulaRefId(name);
  if (subId) {
    if (visitedSubs.has(subId)) return;
    visitedSubs.add(subId);
    const expr = subs?.get(subId)?.expression;
    if (!expr) return;
    try {
      collectIdents(parseFormula(expr), seen, ordered, subs, visitedSubs);
    } catch {
      /* unparseable subformula → skip */
    }
    return;
  }
  if (seen.has(name)) return;
  seen.add(name);
  ordered.push(name);
}

/** Prefer the breakdown date-range bag; fall back to the richest cube. */
export function pickFormulaMetricsBag(
  row: PreviewRow,
): Record<string, number> | null {
  const byRange = row.metricsByDateRange;
  if (!byRange) return null;
  const preferred = row.breakdown?.[0]?.dateRange;
  if (preferred && byRange[preferred] && hasValues(byRange[preferred])) {
    return byRange[preferred];
  }
  return pickLargestBag(byRange);
}

function hasValues(bag: Record<string, number>): boolean {
  return Object.keys(bag).length > 0;
}

function pickLargestBag(
  byRange: Record<string, Record<string, number>>,
): Record<string, number> | null {
  let best: Record<string, number> | null = null;
  let bestKeys = -1;
  for (const bag of Object.values(byRange)) {
    const keys = Object.keys(bag).length;
    if (keys > bestKeys) {
      best = bag;
      bestKeys = keys;
    }
  }
  return bestKeys > 0 ? best : null;
}
